import { getModuleByPath, getNavGroup } from '@solar/shared';
import { ChevronRight, Menu, PanelLeft } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { GlobalSearch } from '@/layouts/GlobalSearch';
import { HelpMenu } from '@/layouts/HelpMenu';
import { NotificationsMenu } from '@/layouts/NotificationsMenu';
import { UserMenu } from '@/layouts/UserMenu';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Hint } from '@/components/ui/tooltip';
import { useUiStore } from '@/store/ui.store';

/**
 * The top bar: navigation triggers and location on the left, global controls on
 * the right.
 *
 * It shows the section and module — "Distribution › Dispatch" — rather than
 * repeating the page title. The page owns its title through `PageHeader`; the top
 * bar answers a different question, which is where in the application you are.
 */
export const Topbar = () => {
  const { pathname } = useLocation();
  const setMobileSidebarOpen = useUiStore((state) => state.setMobileSidebarOpen);
  const toggleSidebar = useUiStore((state) => state.toggleSidebar);

  const currentModule = getModuleByPath(pathname);
  const currentGroup = currentModule ? getNavGroup(currentModule.group) : undefined;
  // A standalone group is a wrapper around one entry — naming it would read as
  // "Business › Tenders", which tells the user nothing they cannot already see.
  const showGroup = currentGroup && !currentGroup.standalone;

  return (
    <header className="sticky top-0 z-20 flex h-topbar items-center gap-2 border-b border-border bg-card/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Open navigation"
        onClick={() => setMobileSidebarOpen(true)}
        className="text-muted-foreground lg:hidden"
      >
        <Menu />
      </Button>

      <Hint label="Toggle sidebar">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Toggle sidebar"
          onClick={toggleSidebar}
          className="hidden text-muted-foreground lg:inline-flex"
        >
          <PanelLeft />
        </Button>
      </Hint>

      {currentModule ? (
        <nav aria-label="Location" className="flex min-w-0 items-center gap-1 text-sm">
          {showGroup ? (
            <>
              <span className="hidden text-muted-foreground md:inline">{currentGroup.label}</span>
              <ChevronRight
                aria-hidden
                className="hidden size-3.5 shrink-0 text-muted-foreground/60 md:inline"
              />
            </>
          ) : null}
          <span className="truncate font-medium">{currentModule.label}</span>
        </nav>
      ) : null}

      <div className="ml-auto flex items-center gap-1.5">
        <div className="hidden md:block">
          <GlobalSearch />
        </div>

        <HelpMenu />
        <NotificationsMenu />

        <Separator orientation="vertical" className="mx-0.5 h-6" />

        <UserMenu />
      </div>
    </header>
  );
};
